import { MenuColors } from '../utils/menuColors';
import axios from 'axios';

// Detecção automática do ambiente
const getApiBaseUrl = () => {
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                       window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1' ||
                       window.location.port === '3002';

  const baseUrl = isDevelopment
    ? (process.env.REACT_APP_API_URL_LOCAL || 'http://localhost:8080')
    : (process.env.REACT_APP_API_URL_PROD || 'https://backend.mvpdelivery.com');

  return `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const orderLinkApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface Store {
  id: string;
  name: string;
  endereco?: string;
  meios_pagamento?: string;
  cobrar_preco_maior?: boolean;
  hora_fechamento?: string;
  pedido_minimo?: number;
  instagram_link?: string;
  facebook_link?: string;
  twitter_link?: string;
  image_path?: string;
  banner_image_path?: string;
  cores_cardapio?: MenuColors | null;
  pix_mp_enabled?: boolean;
}

export interface LinkValidation {
  valid: boolean;
  store?: Store;
  error?: string;
}

export interface ProductTypeInput {
  id: string;
  product_type_id: string;
  name: string;
  input_type: 'string' | 'checkbox' | 'dropdown' | 'fixed_checkbox';
  type_description?: string;
  values?: string[];
  preco_diferente?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductType {
  id: string;
  type: string;
  attributes: string[];
  product_type_inputs: ProductTypeInput[];
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  user_id: string;
  product_type_id?: string | null;
  product_type?: ProductType;
  attributes: Record<string, any>;
  eh_promocao?: boolean;
  description?: string | null;
  is_combo?: boolean;
  combo_items?: Array<{ product_id: string; name: string; quantity: number }>;
  menu_position?: number | null;
  image_path?: string;
  created_at: string;
  updated_at: string;
}

export interface Menu {
  [category: string]: Product[];
}

export interface MenuResponse {
  menu: Menu;
  category_order?: string[];
  total_products: number;
}

export interface OrderItem {
  product_id: string;
  quantity: number;
  product_variables?: { [key: string]: any };
}

export interface CreateOrderData {
  nome_cliente: string;
  endereco: string;
  meio_pagamento: string;
  troco?: string;
  observacoes?: string;
  retirada_local?: boolean;
  items: OrderItem[];
}

export interface OrderResponse {
  success: boolean;
  order: any;
  message: string;
  pix_mp_pending?: boolean;
  pix_token?: string;
}

export interface OrderHistoryItem {
  product_id: string;
  product_name: string;
  quantity: number;
  product_variables: { [key: string]: any };
  unit_price: number;
  total_price: number;
}

export interface OrderHistory {
  id: string;
  daily_order_id: number;
  created_at: string;
  status: string;
  order_price: number;
  meio_pagamento: string;
  retirada_local: boolean;
  items: OrderHistoryItem[];
  total_items: number;
}

export interface OrderHistoryResponse {
  orders: OrderHistory[];
}

export interface ClientAddress {
  id: string;
  client_id: string;
  apelido: string | null;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string | null;
  complemento: string | null;
}

export interface ClientAddressInput {
  apelido?: string;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep?: string;
  complemento?: string;
}

export const buildFullAddress = (addr: ClientAddress | ClientAddressInput): string => {
  return `${addr.rua}, ${addr.numero} - ${addr.bairro}, ${addr.cidade} - ${addr.estado}`;
};

export interface FreteInfo {
  can_deliver: boolean;
  distancia_km: number;
  valor_frete: number;
  max_distance: number;
}

/**
 * Valida um link de pedido
 */
export const validateOrderLink = async (linkId: string): Promise<LinkValidation> => {
  try {
    const response = await orderLinkApi.get(`/order-links/${linkId}/validate`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      valid: false,
      error: 'Erro ao validar link'
    };
  }
};

/**
 * Obtém o cardápio completo de uma loja através de um link válido
 */
export const getMenuByLink = async (linkId: string): Promise<MenuResponse> => {
  const response = await orderLinkApi.get(`/order-links/${linkId}/menu`);
  return response.data;
};

/**
 * Cria um pedido através de um link válido
 */
export const createOrderFromLink = async (
  linkId: string,
  orderData: CreateOrderData
): Promise<OrderResponse> => {
  const response = await orderLinkApi.post(`/order-links/${linkId}/order`, orderData);
  return response.data;
};

/**
 * Obtém o histórico de pedidos do cliente através de um link válido
 */
export const getClientOrders = async (linkId: string): Promise<OrderHistoryResponse> => {
  const response = await orderLinkApi.get(`/order-links/${linkId}/orders`);
  return response.data;
};

/**
 * Obtém os endereços salvos do cliente
 */
export const getClientAddresses = async (linkId: string): Promise<{ addresses: ClientAddress[] }> => {
  const response = await orderLinkApi.get(`/order-links/${linkId}/addresses`);
  return response.data;
};

/**
 * Cria um novo endereço para o cliente
 */
export const createClientAddress = async (linkId: string, data: ClientAddressInput): Promise<{ address: ClientAddress }> => {
  const response = await orderLinkApi.post(`/order-links/${linkId}/addresses`, data);
  return response.data;
};

/**
 * Calcula o frete para um endereço
 */
export const calculateFrete = async (
  linkId: string,
  endereco: string,
  structured?: { bairro?: string; cidade?: string; estado?: string }
): Promise<FreteInfo> => {
  const response = await orderLinkApi.post(`/order-links/${linkId}/calculate-frete`, {
    endereco,
    ...structured
  });
  return response.data;
};

/**
 * Cria um pagamento PIX para um pedido
 */
export const createPixPayment = async (pixToken: string) => {
  const response = await orderLinkApi.post('/pix/create', { pix_token: pixToken });
  return response.data;
};

/**
 * Consulta o status de um pagamento PIX
 */
export const getPixPaymentStatus = async (orderPaymentId: string) => {
  const response = await orderLinkApi.get(`/pix/status/${orderPaymentId}`);
  return response.data;
};

export const orderLinkAPI = {
  validateOrderLink,
  getMenuByLink,
  createOrderFromLink,
  getClientOrders,
  calculateFrete,
  getClientAddresses,
  createClientAddress,
};

export default orderLinkAPI;
